import React, { useEffect, useState, useMemo } from "react";
import {
  Card, Table, Badge, Form, Row, Col, Button,
  InputGroup, Pagination, Modal
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  LuSearch, LuPackage, LuChevronLeft, LuChevronRight,
  LuCircleDot, LuPencil, LuTrash2, LuTag, LuBarcode, LuPlus
} from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteProduct,
  getProductList,
  updateProduct,
} from "../../../redux/reducers/productSlice";
import AddProduct from "./AddProduct";
import ConfirmModal from "../../../components/ConfirmModal";
import { formatCurrency } from "../../../utils/currency";

const ITEMS_PER_PAGE_OPTIONS = [5, 10, 15, 20];

export default function ProductList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedBusiness = useSelector((state) => state.business.selectedBusiness);
  const productdata = useSelector((state) => state.product.productList);
  const currency = useSelector((state) => state.business?.currencyContext?.currency || 'CAD');
 const businessCurrency = useSelector((state) => state.business || 'CAD');
  console.log("currency", businessCurrency);
  useEffect(() => {
    if (selectedBusiness?._id) {
      dispatch(getProductList(selectedBusiness._id));
    }
  }, [dispatch, selectedBusiness]);

  // --- FILTER STATE ---
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedType, setSelectedType] = useState("all");

  // --- PAGINATION STATE ---
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // --- DELETE STATE ---
  const [deletemodalshow, setDeletemodalshow] = useState(false);
  const [deleteid, setDeleteid] = useState(null);

  // --- INVENTORY MODAL STATE ---
  const [inventoryModalShow, setInventoryModalShow] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newQuantity, setNewQuantity] = useState(0);
  const [updatingInventory, setUpdatingInventory] = useState(false);

  // --- DERIVED: unique categories ---
  const categories = useMemo(() => {
    if (!productdata) return [];
    const unique = new Map();
    productdata.forEach((p) => {
      if (p.productCategory?._id)
        unique.set(p.productCategory._id, p.productCategory.name);
    });
    return Array.from(unique.entries()).map(([id, name]) => ({ id, name }));
  }, [productdata]);

  // --- DERIVED: filtered list ---
  const filteredProducts = useMemo(() => {
    if (!productdata) return [];
    return productdata.filter((p) => {
      const matchesSearch = p.name?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        selectedCategory === "all" || p.productCategory?._id === selectedCategory;
      const matchesType =
        selectedType === "all" || p.productType === selectedType;
      return matchesSearch && matchesCategory && matchesType;
    });
  }, [productdata, searchQuery, selectedCategory, selectedType]);

  // --- PAGINATION ---
  const totalPages = Math.ceil(filteredProducts.length / limit);
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * limit;
    return filteredProducts.slice(start, start + limit);
  }, [filteredProducts, currentPage, limit]);

  // Reset to page 1 on filter change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, selectedCategory, selectedType, limit]);

  const hasActiveFilters =
    searchQuery || selectedCategory !== "all" || selectedType !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("all");
    setSelectedType("all");
  };

  // --- DELETE ---
  const handleDelete = () => {
    dispatch(deleteProduct(deleteid)).then((res) => {
      if (res.payload.status !== "error") {
        setDeletemodalshow(false);
      }
    });
  };

  const handleUpdateInventory = () => {
    if (!selectedProduct) return;
    setUpdatingInventory(true);
    
    // Add the new quantity to existing quantity
    const finalQuantity = (selectedProduct.quantity || 0) + newQuantity;

    dispatch(updateProduct({ 
      id: selectedProduct._id, 
      quantity: finalQuantity,
      business: selectedBusiness?._id
    })).then((res) => {
      setUpdatingInventory(false);
      if (res.payload.status !== "error") {
        setInventoryModalShow(false);
        dispatch(getProductList(selectedBusiness?._id));
      }
    });
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case "service":
        return <Badge bg="info-subtle" className="text-info rounded-pill px-3">Service</Badge>;
      default:
        return <Badge bg="success-subtle" className="text-success rounded-pill px-3">Item</Badge>;
    }
  };

  return (
    <div className="mt-4">

      {/* --- FILTER BAR --- */}
      <Card className="border-0 shadow-sm rounded-4 p-3 mb-4">
        <Row className="g-3 align-items-end">

          <Col lg={3} md={6}>
            <Form.Label className="small fw-bold text-muted mb-1">Search</Form.Label>
            <InputGroup className="bg-light rounded-pill overflow-hidden border-0">
              <InputGroup.Text className="bg-transparent border-0 pe-0 text-muted">
                <LuSearch size={16} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search product..."
                className="bg-transparent border-0 shadow-none py-2 small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
          </Col>

          <Col lg={3} md={6}>
            <Form.Label className="small fw-bold text-muted mb-1">Category</Form.Label>
            <Form.Select
              className="bg-light border-0 rounded-pill shadow-none small fw-bold py-2"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </Form.Select>
          </Col>

          <Col lg={2} md={6}>
            <Form.Label className="small fw-bold text-muted mb-1">Type</Form.Label>
            <Form.Select
              className="bg-light border-0 rounded-pill shadow-none small fw-bold py-2"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="service">Service</option>
              <option value="item">Item</option>
            </Form.Select>
          </Col>

          {hasActiveFilters && (
            <Col lg="auto">
              <Button
                variant="outline-secondary"
                className="rounded-pill px-3 small fw-bold py-2"
                onClick={clearFilters}
              >
                Clear Filters
              </Button>
            </Col>
          )}

          <Col lg="auto" className="ms-auto">
            <div className="text-muted small fw-medium text-end">
              <strong>{filteredProducts.length}</strong> of <strong>{productdata?.length ?? 0}</strong> products
            </div>
          </Col>

        </Row>
      </Card>

      {/* --- TABLE --- */}
      <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
        <Card.Body className="p-0">
          <Table responsive hover className="align-middle mb-0">
            <thead className="bg-light text-muted small text-uppercase">
              <tr>
                <th className="ps-4 py-3 fw-semibold">Product</th>
                <th className="fw-semibold">Category</th>
                <th className="fw-semibold">Barcode</th>
                <th className="fw-semibold">Type</th>
                <th className="fw-semibold">Quantity</th>
                <th className="fw-semibold">Price</th>
                <th className="pe-4 text-end fw-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedProducts.length > 0 ? (
                paginatedProducts.map((product) => (
                  <tr key={product._id}>

                    {/* Product */}
                    <td className="ps-4 py-3">
                      <div className="d-flex align-items-center gap-3" style={{ cursor: 'pointer' }} onClick={() => navigate(`/product-detail/${product._id}`)}>
                        <div className="p-0 bg-light rounded-3 text-muted overflow-hidden d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                          {product?.images?.length > 0 ? (
                            <img 
                              src={product.images[0]} 
                              alt={product.name} 
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <LuPackage size={20} />
                          )}
                        </div>
                        <div>
                          <div className="fw-bold small ">{product?.name}</div>
                          <div className="text-muted" style={{ fontSize: "11px" }}>
                            ID: #{product?._id?.slice(-6).toUpperCase()}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Category */}
                    <td>
                      {product?.productCategory?.name ? (
                        <Badge bg="light" className="text-dark border rounded-pill fw-normal px-2 py-1 small d-inline-flex align-items-center gap-1">
                          <LuTag size={11} />
                          {product.productCategory.name}
                        </Badge>
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>

                    {/* Barcode */}
                    <td>
                      {product?.barcode?.url ? (
                        <div className="d-flex flex-column align-items-center gap-1">
                          <a href={product.barcode.url} target="_blank" rel="noreferrer" title="View Full Barcode">
                            <img 
                              src={product.barcode.url} 
                              alt="Barcode" 
                              className="border rounded p-1 bg-white"
                              style={{ maxHeight: '30px', maxWidth: '100px', objectFit: 'contain' }}
                            />
                          </a>
                          <div className="text-muted" style={{ fontSize: '10px', fontFamily: 'monospace' }}>
                            {product.barcode.sku}
                          </div>
                        </div>
                      ) : (
                        <div className="text-muted small d-flex align-items-center gap-1">
                           <LuBarcode size={14} className="opacity-50" />
                           <span>Pending</span>
                        </div>
                      )}
                    </td>

                    {/* Type */}
                    <td>{getTypeBadge(product?.productType)}</td>

                    {/* Quantity */}
                    <td>
                      <span className="small fw-semibold">
                        {product?.productType === "service" ? (
                          <span className="text-muted">N/A</span>
                        ) : (
                          product?.quantity ?? "—"
                        )}
                      </span>
                    </td>

                    {/* Price */}
                    <td>
                      <div className="fw-bold small">
                        {product?.pricingTiers?.length > 0 ? (
                          <>
                            <span className="text-muted extra-small fw-normal me-1">From</span>
                            {formatCurrency(Math.min(product.price, ...product.pricingTiers.map(t => t.price)), currency)}
                          </>
                        ) : (
                          formatCurrency(product?.price, currency)
                        )}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="pe-4 text-end">
                      <div className="d-flex justify-content-end align-items-center gap-2">
                        {product.productType === 'item' && (
                          <Button
                            variant="light"
                            size="sm"
                            className="rounded-circle p-2 text-success bg-success-subtle border-0"
                            title="Update Inventory"
                            onClick={() => {
                              setSelectedProduct(product);
                              setNewQuantity(product.quantity || 0);
                              setInventoryModalShow(true);
                            }}
                          >
                            <LuPlus size={16} />
                          </Button>
                        )}
                        <Button
                          variant="light"
                          size="sm"
                          className="rounded-circle p-2 text-primary bg-primary-subtle border-0"
                          title="View & Edit Product"
                          onClick={() => navigate(`/product-detail/${product._id}`)}
                        >
                          <LuPencil size={15} />
                        </Button>
                        <Button
                          variant="light"
                          size="sm"
                          className="rounded-circle p-2"
                          title="Delete product"
                          onClick={() => {
                            setDeleteid(product?._id);
                            setDeletemodalshow(true);
                          }}
                        >
                          <LuTrash2 size={15} className="text-danger" />
                        </Button>
                      </div>
                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-5 text-muted">
                    <LuCircleDot size={40} className="mb-2 opacity-25" />
                    <p className="mb-0 small">
                      {hasActiveFilters
                        ? "No products match your filters."
                        : "No products found."}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>

        {/* --- PAGINATION FOOTER --- */}
        {filteredProducts.length > 0 && (
          <Card.Footer className="bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center gap-2">
                <small className="text-muted fw-medium">Rows per page:</small>
                <Form.Select
                  size="sm"
                  className="border-0 bg-light rounded-3 fw-bold py-1 px-2"
                  style={{ width: "70px", fontSize: "0.8rem" }}
                  value={limit}
                  onChange={(e) => { setLimit(Number(e.target.value)); setCurrentPage(1); }}
                >
                  {ITEMS_PER_PAGE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </Form.Select>
              </div>
              <small className="text-muted fw-medium border-start ps-3">
                Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                &nbsp;({filteredProducts.length} total)
              </small>
            </div>

            <Pagination
              className="mb-0 gap-1"
              style={{
                "--bs-pagination-active-bg": "#28B0C4",
                "--bs-pagination-active-border-color": "#28B0C4",
              }}
            >
              <Pagination.Prev
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="border-0 shadow-none"
              >
                <LuChevronLeft />
              </Pagination.Prev>
              {[...Array(totalPages)].map((_, idx) => (
                <Pagination.Item
                  key={idx + 1}
                  active={idx + 1 === currentPage}
                  onClick={() => setCurrentPage(idx + 1)}
                >
                  {idx + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="border-0 shadow-none"
              >
                <LuChevronRight />
              </Pagination.Next>
            </Pagination>
          </Card.Footer>
        )}
      </Card>

      <ConfirmModal
        show={deletemodalshow}
        onHide={() => setDeletemodalshow(false)}
        onConfirm={handleDelete}
        title="Delete Product"
        message="Are you sure you want to delete this product? This action is permanent and cannot be undone."
        confirmText="Delete Product"
        variant="danger"
        icon={LuTrash2}
      />

      {/* --- INVENTORY UPDATE MODAL --- */}
      <Modal
        show={inventoryModalShow}
        onHide={() => !updatingInventory && setInventoryModalShow(false)}
        centered
        className="premium-modal"
      >
        <Modal.Header className="border-0 pb-0">
          <Modal.Title className="fw-bold">Update Inventory</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="mb-3">
            <label className="form-label small fw-bold text-muted text-uppercase">Product</label>
            <div className="fw-bold fs-5">{selectedProduct?.name}</div>
            <div className="text-muted small mt-1">
              Current Stock: <span className="fw-bold text-dark">{selectedProduct?.quantity || 0}</span>
            </div>
          </div>
          <div className="mb-4">
            <label className="form-label small fw-bold text-muted text-uppercase">Add New Stock</label>
            <Form.Control
              type="number"
              className="form-control-lg bg-light border-0 fw-bold"
              placeholder="Enter quantity to add..."
              value={newQuantity}
              onChange={(e) => setNewQuantity(parseInt(e.target.value) || 0)}
              autoFocus
            />
            <div className="extra-small text-muted mt-2">
              Resulting total: <span className="fw-bold">{(selectedProduct?.quantity || 0) + newQuantity}</span> units
            </div>
          </div>
          <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top">
            <Button 
              variant="light" 
              className="rounded-pill px-4 fw-bold" 
              onClick={() => setInventoryModalShow(false)}
              disabled={updatingInventory}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              className="rounded-pill px-4 fw-bold" 
              onClick={handleUpdateInventory}
              disabled={updatingInventory}
              style={{ background: "#28B0C4", border: "none" }}
            >
              {updatingInventory ? "Updating..." : "Update Stock"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}